import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { CLOSE_JOB_POST_DETAILS_QUERY } from "@/pages/_layouts/jobs/queries";
import { ProfileSkeleton } from "@/components/Skeleton";
import JobPostCloseForm from "./JobPostCloseForm";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { getDefaultJobPostCloseFormValues } from "./types";
import Button from "@/components/Button";
import { useGetJobDetailsPath } from "@/pages/_layouts/jobs/_utils";
import { useJobPostContext } from "../useJobPostContext";

export default function CloseJobPostPage() {
  const { jobPostId } = useParams<{ jobPostId: string }>();
  const navigate = useNavigate();
  const jobDetailsPath = useGetJobDetailsPath(jobPostId || "");
  const { isJobClosed } = useJobPostContext();

  // Handle navigation
  const handleSuccess = () => navigate(jobDetailsPath);

  // Fetch job post details
  const { data, loading, error } = useQuery(CLOSE_JOB_POST_DETAILS_QUERY, {
    variables: { jobPostId },
    skip: !jobPostId
  });

  const jobHasBeenClosed = isJobClosed(data?.jobPost);
  if (jobHasBeenClosed) {
    navigate(jobDetailsPath);
  }
  // Show loading or error state
  if (loading || error || !data) {
    return (
      <FocusedPageLayout
        actions={[
          <Button key="back" variant="text" onClick={() => navigate(jobDetailsPath)} icon="arrow_back">
            Back
          </Button>
        ]}
      >
        <div className="mt-8 text-center">
          {loading ? (
            <ProfileSkeleton />
          ) : (
            <>
              <h2 className="text-headline-medium">Error loading job post</h2>
              <p className="text-body-medium mt-2">
                There was a problem loading the job post details. Please try again later.
              </p>
            </>
          )}
        </div>
      </FocusedPageLayout>
    );
  }

  return (
    <div className="mx-auto" data-testid="close-job-page">
      <JobPostCloseForm
        jobPostId={jobPostId!}
        defaultValues={getDefaultJobPostCloseFormValues(data.jobPost)}
        onSuccess={handleSuccess}
        submitText="Close Job Post"
        submitDialogContent="Are you sure you want to close this job post?"
      />
    </div>
  );
}
